import * as Sentry from '@sentry/browser';

import { createMatchSelector, push } from 'connected-react-router';
import {
  getDeliveryFee,
  orderDisabled,
  recentOrders as recentOrdersMainLayout,
  refreshCart,
  updateItemCart,
} from 'containers/MainLayout/actions';
import { getStore, getStoreWithRetry } from 'containers/MainLayout/saga';
import {
  selectBuyer,
  selectCart,
  selectCountPendingOrder,
  selectOrderDisabled,
  selectPendingApprovalMode,
  selectUser,
} from 'containers/MainLayout/selectors';
import { all, call, fork, put, select, take } from 'redux-saga/effects';
import { calculateVoucher, createOrder, createOrderDraft, getOrder, getVouchers } from 'utils/apollo';
import {
  calculateVoucher as calculateVoucherAction,
  checkout as checkoutActions,
  createOrderDraft as createOrderDraftAction,
  orderDraft as orderDraftActions,
  setCheckout,
  setOpenConfirmSameDayDeliveryModal,
  setSomethingChanged,
  voucher as voucherActions,
} from './actions';

import { message } from 'antd';
import messages from 'containers/CheckoutPage/messages';
import { updateInvoiceNote } from 'containers/CheckoutStep2Page/saga';
import MainActionTypes from 'containers/MainLayout/constants';
import moment from 'moment';
import translations from 'translations';
import { CartItem, Voucher } from 'types/schema';
import calculateVAT from 'utils/calculateVAT';
import formDateTimeFromGraphQL from 'utils/formatDateTime';
import triggerGA4EcommerceEvent, { EventName } from 'utils/triggerGA4Event';
import ActionTypes, { VOUCHER_STATUS } from './constants';
import {
  selectCheckout,
  selectCurrentAppliedVoucher,
  selectDiscountedValue,
  selectInvoiceNote,
  selectOrderDraftToken,
  selectVouchers,
} from './selectors';

export const isEqualCartItems = (cartItems: CartItem[], newCartItems: CartItem[]) => {
  if (cartItems?.length !== newCartItems?.length) {
    return false;
  }
  const cartItemsMap = new Map();
  const newCartItemsMap = new Map();

  for (let i = 0; i < cartItems.length; i += 1) {
    cartItemsMap.set(cartItems[i]?.product?.id, {
      ...cartItems[i].product,
      quantity: cartItems[i].quantity,
    });
    newCartItemsMap.set(newCartItems[i]?.product?.id, {
      ...newCartItems[i].product,
      quantity: newCartItems[i].quantity,
    });
  }
  const isNumberItemsInCartChange =
    [...new Set([...cartItemsMap.keys(), ...newCartItemsMap.keys()])].length !== cartItems.length;

  if (isNumberItemsInCartChange) {
    return false;
  }
  const hasProductItemChange = [...cartItemsMap.entries()].some(([key, currentProduct]) => {
    const latestProduct = newCartItemsMap.get(key);

    const currentProductPrice = calculateVAT(
      currentProduct.priceV2?.price || 0,
      currentProduct.priceV2?.vat || 0,
      !!currentProduct.priceV2?.vatIncluded,
    ).beforeVat;
    const latestProductPrice = calculateVAT(
      latestProduct.priceV2?.price || 0,
      latestProduct.priceV2?.vat || 0,
      !!latestProduct.priceV2?.vatIncluded,
    ).beforeVat;

    if (currentProductPrice !== latestProductPrice || currentProduct.quantity !== latestProduct.quantity) {
      return true;
    }
    return false;
  });
  if (hasProductItemChange) {
    return false;
  }
  return true;
};

function* goToStepFlow(action) {
  const { payload: step } = action;
  const cart = yield select(selectCart());
  const checkout = yield select(selectCheckout());
  let canGoToNextStep = true;
  let isSameDayDelivery = false;
  cart.cartSet.forEach((singleSupplierCart: any) => {
    if (cart?.store?.moq > singleSupplierCart.total) {
      canGoToNextStep = false;
    }
    const deliveryDay = checkout.deliveryDate[singleSupplierCart.supplier.id];
    if (deliveryDay && moment(deliveryDay, 'DD/MM/YYYY').isSame(moment(), 'day')) {
      isSameDayDelivery = true;
    } else {
      isSameDayDelivery = false;
    }
  });

  if (step === 1) {
    yield put(push('/checkout/step/1'));
    return;
  }
  if (canGoToNextStep) {
    if (step === 2) {
      yield put(push('/checkout/step/2'));
      triggerGA4EcommerceEvent(EventName.BEGIN_CHECKOUT, { ...cart.cartSet[0] });
    }
    if (step === 3) {
      if (isSameDayDelivery) {
        yield put(setOpenConfirmSameDayDeliveryModal(true));
        return;
      }
      const invoiceNote = yield select(selectInvoiceNote());
      const saveInvoiceNoteResponse = yield call(updateInvoiceNote, { payload: { note: invoiceNote } });
      if (saveInvoiceNoteResponse) {
        yield put(createOrderDraftAction());
        triggerGA4EcommerceEvent(EventName.ADD_SHIPPING_INFO, { ...cart.cartSet[0] });
      }
    }
  } else {
    message.error(translations(messages.underMOQMessage));
  }
}

function* createOrderDraftFlow() {
  const store = yield call(getStore);
  if (!store) {
    return;
  }
  yield put(orderDraftActions.request());

  const response = yield call(createOrderDraft, {
    input: {
      storeId: store.id,
    },
  });

  if (!response.errors) {
    yield put(orderDraftActions.success(response));
    yield put(push(`/checkout/step/confirm/${response.draftToken}`));
  } else {
    yield put(orderDraftActions.failure(response.errors));
    response.errors.map((e: any) => message.error(e.message));
  }
}
function* handleValidateVoucher(selectedVoucher: Voucher) {
  const store = yield call(getStoreWithRetry);
  const buyer = yield select(selectBuyer());
  const user = yield select(selectUser());
  if (!store) {
    return;
  }
  const draftToken = yield select(selectOrderDraftToken());
  const input = {
    draftOrderId: draftToken,
    storeId: store.id,
    voucherIds: [selectedVoucher.id],
    userId: user.id,
    buyerId: Number(buyer.id),
  };
  const discountedValue = yield call(calculateVoucher, input);
  if (!discountedValue?.errors) {
    return discountedValue;
  }
  return null;
}

function* getVoucher(action) {
  const { payload } = action;
  const store = yield call(getStoreWithRetry);
  const buyer = yield select(selectBuyer());
  const currentCart = yield select(selectCart());
  if (!store) {
    return;
  }
  const draftToken = yield select(selectOrderDraftToken());
  const input = {
    storeId: store?.internalId || 0,
    buyerId: Number(buyer.id),
    storeUuid: store.id,
    draftOrderId: draftToken,
    cartTotal: payload?.total,
    itemUuids: currentCart?.cartSet?.[0]?.items?.map((item) => item?.product?.id) || [],
  };
  try {
    yield put(voucherActions.request());
    const response = yield call(getVouchers, input);
    if (!response?.errors) {
      yield put(voucherActions.success({ data: response?.data }));
    } else {
      yield put(voucherActions.failure(response.errors));
      response.errors.map((e: any) => message.error(e.message));
    }
  } catch (error) {
    yield put(voucherActions.failure(new Error(`Unexpected error: ${error}`)));
  }
}

function* setVoucher(action) {
  const { payload } = action;
  if (payload?.code && payload?.total) {
    const vouchers = yield select(selectVouchers());
    const currentAppliedVoucher = yield select(selectCurrentAppliedVoucher());
    // const voucherStatus = yield select(selectVoucherStatus());
    let selectedVoucher: Voucher = {};
    if (!vouchers?.length) {
      return yield put({
        type: ActionTypes.SET_VOUCHER,
        payload: {
          currentAppliedVoucher: null,
          status: VOUCHER_STATUS.INVALID,
          discountedValue: -1,
          loading: false,
        },
      });
    }
    if (currentAppliedVoucher || (vouchers && vouchers.length > 0)) {
      if (currentAppliedVoucher && currentAppliedVoucher.code === payload.code) {
        selectedVoucher = currentAppliedVoucher;
      } else {
        selectedVoucher = vouchers.find((voucher) => voucher.code === payload.code);
      }
      if (selectedVoucher) {
        if (!selectedVoucher.isDisabled) {
          yield put(calculateVoucherAction.request());
          const discountedValue = yield call(handleValidateVoucher, selectedVoucher);
          if (discountedValue) {
            if (discountedValue.total > 0) {
              return yield put({
                type: ActionTypes.SET_VOUCHER,
                payload: {
                  currentAppliedVoucher: selectedVoucher,
                  status: VOUCHER_STATUS.APPLIED,
                  discountedValue: discountedValue.total,
                  loading: false,
                },
              });
            }
            if (
              discountedValue.total <= 0 &&
              selectedVoucher.discountMinPrice != null &&
              selectedVoucher.discountMinPrice <= payload.total
            ) {
              return yield put({
                type: ActionTypes.SET_VOUCHER,
                payload: {
                  currentAppliedVoucher: selectedVoucher,
                  status: VOUCHER_STATUS.APPLIED,
                  discountedValue: 0,
                  loading: false,
                },
              });
            }
          }
        }
        const isExpired = Date.now() > new Date(formDateTimeFromGraphQL(selectedVoucher.endDate)).getTime();
        if (isExpired) {
          return yield put({
            type: ActionTypes.SET_VOUCHER,
            payload: {
              currentAppliedVoucher: selectedVoucher.isDisabled ? null : selectedVoucher,
              status: VOUCHER_STATUS.EXPIRED,
              discountedValue: -1,
              loading: false,
            },
          });
        }
        if (selectedVoucher.discountMinPrice && selectedVoucher.discountMinPrice > payload.total) {
          return yield put({
            type: ActionTypes.SET_VOUCHER,
            payload: {
              currentAppliedVoucher: selectedVoucher.isDisabled ? null : selectedVoucher,
              status: VOUCHER_STATUS.NOT_SATISFIED,
              discountedValue: -1,
              loading: false,
            },
          });
        }
        return yield put({
          type: ActionTypes.SET_VOUCHER,
          payload: {
            currentAppliedVoucher: selectedVoucher.isDisabled ? null : selectedVoucher,
            status: VOUCHER_STATUS.INVALID,
            discountedValue: -1,
            loading: false,
          },
        });
      }
      return yield put({
        type: ActionTypes.SET_VOUCHER,
        payload: {
          currentAppliedVoucher: null,
          status: VOUCHER_STATUS.INVALID,
          discountedValue: -1,
          loading: false,
        },
      });
    }
    return yield put({
      type: ActionTypes.SET_VOUCHER,
      payload: {
        currentAppliedVoucher: null,
        status: null,
        discountedValue: -1,
      },
    });
  }
  return yield put({
    type: ActionTypes.SET_VOUCHER,
    payload: {
      currentAppliedVoucher: null,
      status: null,
      discountedValue: -1,
      loading: false,
    },
  });
}

function* checkoutFlow() {
  yield put(checkoutActions.request());
  const currentAppliedVoucher = yield select(selectCurrentAppliedVoucher());

  let draftToken = yield select(selectOrderDraftToken());
  const currentCart = yield select(selectCart());

  yield put(refreshCart());
  yield all([take(MainActionTypes.FETCH_CART_SUCCESS), take(MainActionTypes.FETCH_ORDER_DISABLED_SUCCESS)]);

  // Lấy giỏ hàng đã cập nhật từ state
  const orderDisabledResponse = yield select(selectOrderDisabled());
  if (orderDisabledResponse.orderDisabled) {
    message.error(messages.orderDisabled);
    yield put(checkoutActions.failure());
    return;
  }
  const cart = yield select(selectCart());

  if (
    cart.notAvailableProducts?.length > 0 ||
    cart.cartSet[0].total < cart?.store?.moq ||
    !isEqualCartItems(cart?.cartSet[0].items, currentCart?.cartSet[0].items)
  ) {
    yield put(checkoutActions.failure());
    yield put(setSomethingChanged());
    return;
  }

  const store = yield call(getStore);
  if (!store) {
    return;
  }
  if (!draftToken) {
    const route = yield select(createMatchSelector('/checkout/step/confirm/:slug'));
    draftToken = route.params.slug;
  }

  const totalAfterAppliedVouchers = yield select(selectDiscountedValue());
  const input = {
    draftToken: draftToken,
    storeId: store.id,
    voucherIds: currentAppliedVoucher ? [currentAppliedVoucher.id] : [],
    totalAfterAppliedVouchers: totalAfterAppliedVouchers,
  };
  const response = yield call(createOrder, input);
  if (response.errors) {
    yield put(checkoutActions.failure(response.errors));
    for (const e of response.errors) {
      if (e.message.toLowerCase().includes('voucher') && currentAppliedVoucher) {
        yield put({
          type: ActionTypes.SET_VOUCHER,
          payload: {
            currentAppliedVoucher: currentAppliedVoucher,
            status: VOUCHER_STATUS.INVALID,
            discountedValue: -1,
          },
        });
      } else {
        message.error(e.message);
      }
    }
  } else {
    yield put(
      checkoutActions.success({
        status: 'CHECKOUT_SUCCESS',
        orders: response.orders,
      }),
    );
    yield put(updateItemCart.success(response.cart));
    const isPending = yield select(selectPendingApprovalMode());
    if (isPending) {
      const currentCount = yield select(selectCountPendingOrder());
      yield put(recentOrdersMainLayout.success({ countPendingOrders: currentCount + 1 }));
    }
    yield put(
      setCheckout({
        deliveryNotes: [],
        quantity: {},
        totalOfSupplier: {},
        totalBeforeVATOfSupplier: {},
        timeSlot: {},
        deliveryDate: {},
        total: 0,
      }),
    );
    yield put({
      type: ActionTypes.SET_VOUCHER,
      payload: {
        currentAppliedVoucher: null,
        status: null,
        discountedValue: -1,
      },
    });
    window.localStorage.setItem('dayDelivery', '');
    triggerGA4EcommerceEvent(EventName.PURCHASE, {
      ...cart.cartSet[0],
      id: response?.orders[0].id,
    });
  }
}

function* onTrackOrdersCompleted(action) {
  const response = yield all([...action.payload.map((orderId) => call(getOrder, { id: orderId }))]);

  if (response.errors) {
    Sentry.captureException(response.errors);
  }
}

function* getDeliveryFeeFlow() {
  while (true) {
    yield take(ActionTypes.SET_CHECKOUT_TOTAL);
    const checkoutData = yield select(selectCheckout());
    yield put(getDeliveryFee.request({ totalPrice: checkoutData?.total }));
  }
}

export default function* checkoutPageSaga() {
  yield all(
    [
      [ActionTypes.CREATE_ORDER_DRAFT, createOrderDraftFlow],
      [ActionTypes.GET_VOUCHER, getVoucher],
      [ActionTypes.APPLY_VOUCHER, setVoucher],
      [ActionTypes.CHECKOUT_CART, checkoutFlow],
      [ActionTypes.GO_TO_STEP, goToStepFlow],
      [ActionTypes.TRACK_ORDERS_COMPLETED, onTrackOrdersCompleted],
    ].map(([actionName, handler]) =>
      fork(function* () {
        while (true) {
          const action = yield take(actionName as any);
          yield call(handler as any, action as any);
        }
      }),
    ),
  );
  yield fork(getDeliveryFeeFlow);
  yield put(orderDisabled.request());
}
